import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, Search } from "lucide-react";
import { PageLoading, PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const GRADE_BADGE = "rounded-full bg-cyan/10 px-2 py-0.5 text-[11px] font-semibold text-cyan-dark";
const STATUS_C = { ACTIVE: "bg-emerald-50 text-emerald-600", ARCHIVED: "bg-slate-100 text-slate-500", HOSPITALIZED: "bg-amber-50 text-amber-700", PAUSED: "bg-amber-50 text-amber-700", INACTIVE: "bg-slate-100 text-slate-500", DECEASED: "bg-slate-100 text-slate-500" };

function age(dob) { if (!dob) return "—"; try { return Math.floor((Date.now() - new Date(dob)) / 3.15576e10); } catch { return "—"; } }

export default function CarePatients() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [grade, setGrade] = useState("");
  const nav = useNavigate();

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (status) p.set("status", status); if (grade) p.set("care_grade", grade);
    api.get(`/care/patients?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ patients: [] }));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, grade]);
  if (!data) return <PageLoading />;

  return (
    <div data-testid="care-patients-page">
      <PageHeader title="Patienten" subtitle="Patientenverwaltung mit unveränderbarer Patienten-ID."
        action={<button onClick={() => nav("/app/employer/pflege/patients/new")} data-testid="care-new-patient" className="inline-flex items-center gap-1.5 rounded-full bg-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-dark"><Plus className="h-4 w-4" /> Neuer Patient</button>} />

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Patient ID, Name oder Ort…" className="pl-9" data-testid="care-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="care-filter-status" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Alle Status</option>{Object.entries(data.statuses || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="care-filter-grade" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Alle Pflegegrade</option>{Object.entries(data.grades || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="px-4 py-3">Patient ID</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Alter</th><th className="px-4 py-3">Pflegegrad</th><th className="px-4 py-3">Ort</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.patients.map((p) => (
              <tr key={p.id} onClick={() => nav(`/app/employer/pflege/patients/${p.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`care-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.patient_number}</td>
                <td className="px-4 py-3 font-semibold text-navy">{p.first_name} {p.last_name}</td>
                <td className="px-4 py-3 text-slate-600">{age(p.date_of_birth)}</td>
                <td className="px-4 py-3"><span className={GRADE_BADGE}>{p.care_grade_label}</span></td>
                <td className="px-4 py-3 text-slate-600">{p.city || "—"}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status_label}</span></td>
              </tr>
            ))}
            {data.patients.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="care-empty">Keine Patienten gefunden.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
